// /sworn-translator/documents/$group — Wave 65: TH deep-dive per certified-translation document family
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import ServiceAuthorityBlock from "@/components/ServiceAuthorityBlock";
import { SWORN_DOC_GROUPS, type SwornDocGroup } from "@/data/sworn-doc-groups";
import { APOSTILLE_STATUS } from "@/data/apostille-status";
import { ctaDesc, ctaTitle } from "@/lib/seo";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/sworn-translator/documents/$group")({
  loader: async ({ params }) => {
    const { SWORN_DOC_GROUPS } = await import("@/data/sworn-doc-groups");
    const group = SWORN_DOC_GROUPS.find((g) => g.slug === params.group);
    if (!group) throw notFound();
    return {
      slug: group.slug,
      title: group.titleTh,
      summary: group.summaryTh,
      faqs: group.faqsTh.map((f) => ({ q: f.q, a: f.a })),
    };
  },
  head: ({ params, loaderData }) => {
    const g = loaderData;
    if (!g) return {};
    const path = `/sworn-translator/documents/${params.group}`;
    const url = `${SITE}${path}`;
    const enUrl = `${SITE}/en/sworn-translator/documents/${params.group}`;
    const title = `${g.title} — แปลและรับรองคำแปล | NYC`;
    const desc = g.summary.slice(0, 155);
    return {
      meta: [
        { title: ctaTitle(title, path, "th") },
        { name: "description", content: ctaDesc(desc, path, "th") },
        { property: "og:title", content: ctaTitle(title, path, "th") },
        { property: "og:description", content: ctaDesc(desc, path, "th") },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { property: "og:locale", content: "th_TH" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [
        { rel: "canonical", href: url },
        { rel: "alternate", hrefLang: "th-TH", href: url },
        { rel: "alternate", hrefLang: "en", href: enUrl },
        { rel: "alternate", hrefLang: "x-default", href: url },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "@id": `${url}#breadcrumb`,
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "แปลและรับรองคำแปล",
                item: `${SITE}/sworn-translator`,
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "เอกสารที่รับแปลและรับรอง",
                item: `${SITE}/sworn-translator/documents`,
              },
              { "@type": "ListItem", position: 3, name: g.title, item: url },
            ],
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "@id": `${url}#faq`,
            inLanguage: "th",
            mainEntity: g.faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
      ],
    };
  },
  component: SwornDocGroupPage,
});

function SwornDocGroupPage() {
  const { slug } = Route.useLoaderData();
  const g = SWORN_DOC_GROUPS.find((x) => x.slug === slug) as SwornDocGroup;
  const others = SWORN_DOC_GROUPS.filter((x) => x.slug !== g.slug);

  return (
    <main className="mx-auto max-w-4xl px-4 py-10">
      <nav className="mb-4 text-sm text-muted-foreground">
        <Link to="/sworn-translator" className="hover:underline">
          แปลและรับรองคำแปล
        </Link>{" "}
        /{" "}
        <Link to="/sworn-translator/documents" className="hover:underline">
          เอกสารที่รับแปลและรับรอง
        </Link>{" "}
        / {g.titleTh}
      </nav>

      <h1 className="text-3xl font-bold tracking-tight md:text-4xl">{g.titleTh}</h1>
      <p className="mt-4 text-base leading-8 text-muted-foreground" data-speakable>
        {g.summaryTh}
      </p>

      <aside className="mt-6 rounded-lg border border-border bg-muted/40 p-4 text-sm leading-7">
        <strong>สถานะ Apostille ของไทย:</strong> ไทยยื่นภาคยานุวัติสารเมื่อ{" "}
        {APOSTILLE_STATUS.accessionDepositTh} และอนุสัญญาจะมีผลบังคับใช้กับไทยในวันที่{" "}
        {APOSTILLE_STATUS.entryIntoForceTh} ก่อนวันดังกล่าว คำแปลยังต้องผ่านการรับรองของกรมการกงสุลและสถานทูตปลายทางตามลำดับเดิม{" "}
        <a
          href={APOSTILLE_STATUS.sourceUrl}
          target="_blank"
          rel="noopener noreferrer nofollow"
          className="underline"
        >
          ที่มา: HCCH
        </a>
      </aside>

      <div className="mt-8 space-y-4 text-base leading-8">
        {g.bodyTh.map((p, i) => (
          <p key={i}>{p}</p>
        ))}
      </div>

      <h2 className="mt-10 text-2xl font-semibold tracking-tight">เอกสารในกลุ่มนี้</h2>
      <ul className="mt-2 list-disc space-y-1 pl-5 text-sm leading-7">
        {g.documentsTh.map((d) => (
          <li key={d}>{d}</li>
        ))}
      </ul>

      <h2 className="mt-8 text-2xl font-semibold tracking-tight">ลำดับขั้นตอน</h2>
      <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm leading-7">
        {g.chainTh.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ol>

      <h2 className="mt-8 text-2xl font-semibold tracking-tight">เช็กลิสต์เตรียมเอกสาร</h2>
      <ul className="mt-2 list-disc space-y-1 pl-5 text-sm leading-7">
        {g.checklistTh.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ul>

      <h2 className="mt-8 text-2xl font-semibold tracking-tight">คำถามที่พบบ่อย</h2>
      <div className="mt-2 space-y-3">
        {g.faqsTh.map((f) => (
          <div key={f.q} className="rounded-lg border border-border p-4">
            <p className="font-medium voice-faq-q">{f.q}</p>
            <p className="mt-1 text-sm leading-7 text-muted-foreground voice-faq-a">{f.a}</p>
          </div>
        ))}
      </div>

      <p className="mt-8 rounded-lg border border-border bg-muted/30 p-4 text-sm leading-7">
        ต้องการให้เจ้าหน้าที่ประเมินชุดเอกสารของคุณ ส่งภาพสแกนมาก่อนได้ทาง LINE หรืออีเมล
        แล้วทีมงานจะแจ้งขั้นตอนและค่าบริการที่ตรงกับกรณีของคุณกลับไป
      </p>

      <h2 className="mt-10 text-xl font-semibold tracking-tight">กลุ่มเอกสารอื่น</h2>
      <ul className="mt-2 grid gap-2 sm:grid-cols-2">
        {others.map((o) => (
          <li key={o.slug}>
            <Link
              to="/sworn-translator/documents/$group"
              params={{ group: o.slug }}
              className="text-primary hover:underline"
            >
              {o.titleTh}
            </Link>
          </li>
        ))}
      </ul>
          <ServiceAuthorityBlock topic="sworn" locale="th" />
    </main>
  );
}
